import React, { useState, useEffect } from 'react'
import { Box, Paper, SimpleGrid, Checkbox, Text, Divider, Card, Group, Avatar, Select, MultiSelect, Flex, Button } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useSelector, useDispatch } from 'react-redux'
import { useFormUserMembership } from './schema/form'
import { getListUserMemberships, updateUser } from '../../../../../services/users'
import { getAllCountryList } from '../../../../../services/location'
import LoadingData from '../../../../ui/LoadingData'
import { notificationError, notificationSuccess } from '../../../../ui/Notifications'
import { getUserDetail } from '../../../../../store/userDetail'
import { useParams } from 'react-router-dom'

const getFlagEmoji = (countryCode) => {
  if (!countryCode) {
    return ''
  }
  const codePoints = countryCode.toUpperCase().split('').map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

const UserMembership = () => {
  const params = useParams()
  const userId = params.id
  const dispatch = useDispatch()
  const { membership, included } = useSelector((state) => state.user_detail)
  const [loadingForm, setLoadingForm] = useState(false)
  const [loadingMembership, setLoadingMembership] = useState(true)
  const [membershipsList, setMembershipsList] = useState([])
  const [countryList, setCountryList] = useState([])
  const [currencyList, setCurrencyList] = useState([])

  const form = useFormUserMembership()

  const handleSubmitUserMembership = async (form) => {
    setLoadingForm(true)
    try {
      const response = await updateUser(userId, form)
      if (response.success) {
        setLoadingForm(false)
        dispatch(getUserDetail(userId))
        notificationSuccess('Update User Successfully', 'This data has been updated')
      }
    } catch (error) {
      setLoadingForm(false)
      let errorDescriptionMessage = 'There is an error while updating user'
      const errorMessage = error?.response?.data?.message
      if (errorMessage) {
        errorDescriptionMessage = errorMessage
      }
      notificationError('Update User Error', errorDescriptionMessage)
    }
  }

  const handleGetListMemberships = async () => {
    setLoadingMembership(true)
    try {
      const response = await getListUserMemberships()
      if (response.data.success) {
        const dataMembership = response.data.data
        const remapDataMembership = dataMembership.map((val) => {
          return {
            value: `${val.id}`,
            label: val.attributes.name
          }
        })
        setMembershipsList(remapDataMembership)
      }
    } catch (error) {
      console.error("Failed to fetch memberships", error)
    } finally {
      setLoadingMembership(false)
    }
  }

  const handleGetCountryList = async (countryId) => {
    const params = {
      'filter[id][is]': countryId,
      'page[num]': '1',
      'page[limit]': '99999',
    }
    try {
      const response = await getAllCountryList(params)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        const iso = val.attributes.iso_code_2 || val.attributes.iso_code_3 || ''
        return {
          value: `${val.attributes.id}`,
          label: `${getFlagEmoji(iso)} ${val.attributes.name}`.trim()
        }
      })
      setCountryList(remapDataCountry)

      const remapDataCurrency = dataCountry.map((val) => {
        return {
          value: val.attributes.currency,
          label: `${val.attributes.currency} (${val.attributes.name})`
        }
      })
      const filterNullValue = remapDataCurrency.filter((val) => val.value !== null)
      const filterDuplicateCurrency = filterNullValue.reduce((result, item) => {
        const removed = result.filter((val) => val.value !== item.value)
        return [...removed, item]
      }, [])
      setCurrencyList(filterDuplicateCurrency)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    handleGetCountryList()
    handleGetListMemberships()
    if (membership && included) {
      handleSetForm(membership, included)
    }
    // eslint-disable-next-line
  }, [membership, included])

  const handleSetForm = (value, included) => {
    const includedMemberships = Object.values(included.memberships)
    const remapMembership = includedMemberships.map((val) => {
      return `${val.id}`
    })
    const remapCountryCatalog = value.countryCatalog
      ? value.countryCatalog.map((val) => {
        return `${val}`
      })
      : []

    const findDefaultShipping = value.settings.find((val) => val.key === 'default_shipping_to')
    const findCurrency = value.settings.find((val) => val.key === 'currency')

    const remapShippingList = value.shippingUser.map((val) => {
      return `${val}`
    })

    const dataForm = {
      is_agent: value.isAgent,
      is_fulfillment_client: value.isFulfillmentClient,
      is_partner: value.isPartner,
      membership_ids: remapMembership,
      start_at: new Date(value.startDate),
      expired_at: new Date(value.expiredDate),
      country_catalog_active: `${value.activeCountry || ''}`,
      country_catalogs: remapCountryCatalog,
      default_shipping_to: `${findDefaultShipping?.value || ''}`,
      currency: findCurrency?.value || 'IDR',
      shipping_list: remapShippingList
    }
    form.initialize(dataForm)
    form.setValues(dataForm)
  }

  const handlingScrolledError = (error) => {
    const errorField = Object.keys(error)[0]
    if (errorField) {
      const inputNode = form.getInputNode(errorField)
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const loadForm = () => {
    return (
      <Box>
        <SimpleGrid cols={3} spacing='md' verticalSpacing='md' my='xl'>
          <Paper p='sm' withBorder shadow="xs" radius="md">
            <Checkbox
              size='xs'
              fw='600'
              label="Fulfillment Client"
              description={
                <Box h={30}>
                  <Text fz={11} lineClamp={2} >If this is enabled. User will have fulfillment access</Text>
                </Box>
              }
              {...form.getInputProps('is_fulfillment_client', { type: 'checkbox' })}
            />
          </Paper>

          <Paper p='sm' withBorder shadow="xs" radius="md">
            <Checkbox
              size='xs'
              fw='600'
              label="Agent"
              description={
                <Box h={30}>
                  <Text fz={11} lineClamp={2} >If this is enabled. User will become agent</Text>
                </Box>
              }
              {...form.getInputProps('is_agent', { type: 'checkbox' })}
            />
          </Paper>

          <Paper p='sm' withBorder shadow="xs" radius="md">
            <Checkbox
              size='xs'
              fw='600'
              label="Partner"
              description={
                <Box h={30}>
                  <Text fz={11} lineClamp={2} >If this is enabled. User will become partner </Text>
                </Box>
              }
              {...form.getInputProps('is_partner', { type: 'checkbox' })}
            />
          </Paper>
        </SimpleGrid>

        <Paper my='xl' p='md' withBorder shadow='xs' radius='md'>
          <Text fw={500}>
            Membership
          </Text>

          <Divider my='xs' />

          <Checkbox.Group
            size='xs'
            mb='sm'
            key={form.key('membership_ids')}
            {...form.getInputProps('membership_ids')}
            style={{ gridColumn: 'span 2' }}
          >
            <Card withBorder={true} p='sm' mb='md'>
              <SimpleGrid cols={4}>
                {membershipsList.map((item) => (
                  <Checkbox.Card size='xs' value={item.value} key={item.value} withBorder radius="md" p="xs">
                    <Group wrap="nowrap" align="center" gap='xs'>
                      <Checkbox.Indicator />
                      <Avatar size='sm' name={item.label} color='red'  />
                      <Text fz="xs" fw='500'>{item.label}</Text>
                    </Group>
                  </Checkbox.Card>
                ))}
              </SimpleGrid>
            </Card>
          </Checkbox.Group>
          <SimpleGrid cols={3} spacing='md' verticalSpacing='sm' mb='sm'>
            <DatePickerInput
              size='xs'
              label="Member Start Date"
              placeholder="Start Date"
              {...form.getInputProps('start_at')}
            />
            <DatePickerInput
              size='xs'
              label="Member Expired Date"
              placeholder="Expired Date"
              {...form.getInputProps('expired_at')}
            />
          </SimpleGrid>
        </Paper>

        <Paper my='xl' p='md' withBorder shadow='xs' radius='md'>
          <Text fw={500}>
            Catalog User
          </Text>

          <Divider my='xs' />

          <SimpleGrid cols={3} spacing='md' verticalSpacing='sm' mb='sm'>
            <Select
              size='xs'
              allowDeselect={false}
              searchable
              clearable
              label="Active Country"
              placeholder="Select Country"
              data={countryList}
              key={form.key('country_catalog_active')}
              {...form.getInputProps('country_catalog_active')}
            />
          </SimpleGrid>

          <MultiSelect
            size='xs'
            mb='sm'
            label="Country Catalog"
            placeholder="Select Countries"
            data={countryList}
            searchable
            key={form.key('country_catalogs')}
            {...form.getInputProps('country_catalogs')}
            clearable
            nothingFoundMessage='No Country Found'
            hidePickedOptions
          />
        </Paper>

        <Paper my='xl' p='md' withBorder shadow='xs' radius='md'>
          <Text fw={500}>
            Shipping User
          </Text>

          <Divider my='xs' />

          <SimpleGrid cols={3} spacing='md' verticalSpacing='sm' mb='sm'>
            <Select
              size='xs'
              allowDeselect={false}
              searchable
              clearable
              label="Default Shipping to Country"
              placeholder="Select Country"
              data={countryList}
              key={form.key('default_shipping_to')}
              {...form.getInputProps('default_shipping_to')}
            />
            <Select
              size='xs'
              allowDeselect={false}
              searchable
              clearable
              label="Currency"
              placeholder="Select Currency"
              data={currencyList}
              key={form.key('currency')}
              {...form.getInputProps('currency')}
            />
          </SimpleGrid>
          <MultiSelect
            size='xs'
            mb='sm'
            label="Shipping to Country"
            placeholder="Select Countries"
            data={countryList}
            searchable
            key={form.key('shipping_list')}
            {...form.getInputProps('shipping_list')}
            clearable
            nothingFoundMessage='No Country Found'
            hidePickedOptions
          />
        </Paper>

        <Box mt={20}>
          <Flex justify='flex-end'>
            <Button
              size='xs'
              loading={loadingForm}
              variant="filled"
              onClick={form.onSubmit((value) => { 
                handleSubmitUserMembership(value) 
              }, (error) => {
                handlingScrolledError(error) 
              })}
            >
              Update
            </Button>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {loadingMembership ? <LoadingData /> : loadForm()}
    </Box>
  )
}

export default UserMembership